import { useCallback, useEffect, useState } from 'react';
import { AudioEngine } from '../audio/AudioEngine';
import type { AudioEngineSnapshot } from '../types/audio';

const INITIAL_SNAPSHOT: AudioEngineSnapshot = {
  status: 'idle',
  position: 0,
  duration: 0,
  outputMode: 'stereo',
  masterVolume: 1,
  memoryBytes: 0,
  warning: null,
  error: null,
};

export function useAudioEngine() {
  const [engine] = useState(() => new AudioEngine());
  const [snapshot, setSnapshot] = useState<AudioEngineSnapshot>(INITIAL_SNAPSHOT);

  useEffect(() => {
    const unsubscribe = engine.subscribe(setSnapshot);
    let animationFrame = 0;
    const refresh = () => {
      if (engine.getSnapshot().status === 'playing') setSnapshot(engine.getSnapshot());
      animationFrame = requestAnimationFrame(refresh);
    };
    animationFrame = requestAnimationFrame(refresh);
    return () => {
      cancelAnimationFrame(animationFrame);
      unsubscribe();
    };
  }, [engine]);

  const execute = useCallback(
    async (action: (audio: AudioEngine) => void | Promise<void>) => {
      await action(engine);
      setSnapshot(engine.getSnapshot());
    },
    [engine],
  );

  return { engine, snapshot, execute };
}
